import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import {
  FiBell,
  FiChevronDown,
  FiExternalLink,
  FiMoon,
  FiSun,
  FiUserPlus,
} from "react-icons/fi";
import { MdSportsEsports } from "react-icons/md";
import { useTheme } from "hooks/useTheme";
import useLocalStorage from "helpers/useLocalStorage";
import { AFFILIATE_CODE_STORAGE_KEY } from "helpers/authSession";
import { URL_PHOTO_AW3 } from "constants/index";
import { getUserProfileData, userGetMe } from "services/user.service";

const routeMeta: Record<string, { title: string; subtitle: string }> = {
  "/dashboard": {
    title: "ແດສບອດ",
    subtitle: "ສູນລວມຂໍ້ມູນຂອງສະຕຣີມເມີ",
  },
  "/donations": {
    title: "ຈັດການ Donate",
    subtitle: "ຈັດການລາຍການໂດເນດ ແລະ OBS widget",
  },
  "/donations/conditions": {
    title: "ເງື່ອນໄຂ Donate",
    subtitle: "ລາຍລະອຽດການນັບລາຍໄດ້, ຄ່າທຳນຽມ ແລະ ການຂື້ນຈໍ",
  },
  "/donations/widgets": {
    title: "ປັບແຕ່ງວິດເຈັດ",
    subtitle: "ຕັ້ງຄ່າ Alert, Goal, Timer, Leaderboard ແລະ Chat box",
  },
  "/donations/customize": {
    title: "ຂໍ້ມູນຊ່ອງສະຕຣີມ",
    subtitle: "ຕັ້ງຄ່າໂປຣໄຟລ໌, ຮູບພາບ, ລິ້ງ ແລະ ການ Donate",
  },
  "/transaction": {
    title: "Transaction",
    subtitle: "ປະຫວັດລາຍການກະເປົາເງິນ",
  },
  "/game": {
    title: "ຈັດການເຕີມເກມ",
    subtitle: "ເພີ່ມ ແກ້ໄຂ ເປີດ/ປິດ ແລະ ຕັ້ງຄ່າແພັກເກດເຕີມເກມ",
  },
  "/game/conditions": {
    title: "ເງື່ອນໄຂເວັບເຕີມເກມ",
    subtitle: "ລາຍລະອຽດການນັບລາຍໄດ້, order ແລະ ການຈ່າຍ",
  },
  "/withdraw": {
    title: "ແຈ້ງຖອນເງິນ",
    subtitle: "ຈັດການການຖອນເງິນຂອງທ່ານ",
  },
  "/withdraw-history": {
    title: "ປະຫວັດການຖອນເງິນ",
    subtitle: "ລາຍການອະນຸມັດ ແລະ ຄ່າທຳນຽມການຖອນ",
  },
  "/merchant": {
    title: "ບັນຊີຮັບເງິນ",
    subtitle: "ຢືນຢັນໂຕຕົນ ແລະ ຈັດການບັນຊີທະນາຄານສຳລັບຮັບເງິນ",
  },
  "/referrals": {
    title: "ການແນະນຳໝູ່",
    subtitle: "ຈັດການ referral link, ລາຍການແນະນຳ ແລະ ລາຍໄດ້",
  },
  "/referrals/detail": {
    title: "ເງື່ອນໄຂການແນະນຳ",
    subtitle: "ລາຍລະອຽດການນັບ referral, ລາງວັນ ແລະ ການຈ່າຍ",
  },
};

const getRouteMeta = (pathname: string) => {
  const matchedRoute = Object.keys(routeMeta)
    .sort((a, b) => b.length - a.length)
    .find((route) => pathname === route || pathname.startsWith(`${route}/`));

  return matchedRoute
    ? routeMeta[matchedRoute]
    : {
        title: "OneTop",
        subtitle: "Donate portal",
      };
};

const getImageSrc = (value: string) => {
  if (!value) return "";
  if (
    value.startsWith("http") ||
    value.startsWith("/") ||
    value.startsWith("blob:") ||
    value.startsWith("data:")
  ) {
    return value;
  }

  return `${URL_PHOTO_AW3}${value}`;
};

const getInitials = (name: string, username: string) => {
  const source = name || username || "Streamer";
  const initials = source
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return initials || "ST";
};

const getDonateUrl = (username: string) => {
  const cleanUsername = username.trim();
  if (!cleanUsername) return "";

  return `https://donate.onetop.la/${encodeURIComponent(cleanUsername)}`;
};

const getAffiliateUrl = (username: string) => {
  const cleanCode = username.trim();
  if (!cleanCode) return "";
  const origin =
    typeof window === "undefined"
      ? "https://merchant.onetop.la"
      : window.location.origin;

  return `${origin}/register?ref=${encodeURIComponent(cleanCode)}`;
};

const getAffiliateCode = (user: any) =>
  String(
    user?.affiliateCode ||
      user?.affiliate_code ||
      user?.affiliate?.code ||
      user?.affiliate?.affiliateCode ||
      user?.affiliate?.referralCode ||
      user?.referralCode ||
      user?.referral_code ||
      user?.referral?.code ||
      user?.referral?.referralCode ||
      user?.refCode ||
      user?.ref_code ||
      user?.inviteCode ||
      user?.invite_code ||
      "",
  ).trim();

const getAffiliateCodeFromResponse = (response: any) => {
  const payloads = [
    response,
    response?.data,
    response?.data?.user,
    response?.user,
    response?.profile,
    response?.data?.profile,
    getUserProfileData(response),
  ];

  for (const payload of payloads) {
    const code = getAffiliateCode(payload);
    if (code) return code;
  }

  return "";
};

export default function NavTest() {
  const location = useLocation();
  const navigate = useNavigate();
  const meta = getRouteMeta(location.pathname);
  const { isDark, toggleTheme } = useTheme();
  const [token] = useLocalStorage("token", "");
  const [businessId] = useLocalStorage("businessId", "");
  const [storedAffiliateCode, setStoredAffiliateCode] = useLocalStorage(
    AFFILIATE_CODE_STORAGE_KEY,
    "",
  );
  const { data: userResponse } = useQuery(
    ["user-me", businessId || "self"],
    () => userGetMe({ token, businessId }),
    { enabled: Boolean(token), retry: false },
  );
  const user = getUserProfileData(userResponse);
  const displayName = user?.name || user?.displayName || user?.username || "";
  const username = user?.username || user?.email || "";
  const avatarSrc = getImageSrc(
    user?.image || user?.avatarUrl || user?.profileImage || user?.avatar || "",
  );
  const initials = getInitials(displayName, username);
  const donateUrl = getDonateUrl(user?.username || "");
  const affiliateCode =
    getAffiliateCodeFromResponse(userResponse) ||
    storedAffiliateCode ||
    user?.username ||
    "";
  const affiliateUrl = getAffiliateUrl(affiliateCode);

  React.useEffect(() => {
    const responseAffiliateCode = getAffiliateCodeFromResponse(userResponse);
    if (
      responseAffiliateCode &&
      responseAffiliateCode !== storedAffiliateCode
    ) {
      setStoredAffiliateCode(responseAffiliateCode);
    }
  }, [setStoredAffiliateCode, storedAffiliateCode, userResponse]);

  return (
    <header className="border-b border-[#e6ebf1] bg-white transition-colors dark:border-slate-800 dark:bg-slate-950">
      <nav className="flex h-[64px] items-center justify-between gap-2 px-3 lg:gap-4 lg:px-6">
        <div className="flex min-w-0 items-center gap-2">
          <div className="min-w-0">
            <h2 className="m-0 truncate text-[15px] font-bold text-[#101828] dark:text-slate-100">
              {meta.title}
            </h2>
            <p className="m-0 mt-0.5 hidden truncate text-xs text-[#667085] dark:text-slate-400 sm:block">
              {meta.subtitle}
            </p>
          </div>
        </div>

        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <button
            type="button"
            className="flex h-9 w-9 items-center justify-center rounded-full text-[#475467] transition hover:bg-[#eef7ff] hover:text-[#168feb] dark:text-slate-300 dark:hover:bg-slate-900 dark:hover:text-primary-300"
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            title={isDark ? "Light mode" : "Dark mode"}
            onClick={toggleTheme}
          >
            {isDark ? (
              <FiSun className="h-4 w-4" />
            ) : (
              <FiMoon className="h-4 w-4" />
            )}
          </button>

          {donateUrl ? (
            <>
              <a
                href={donateUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-9 w-9 items-center justify-center rounded-full text-[#475467] no-underline transition hover:bg-[#eef7ff] hover:text-[#168feb] dark:text-slate-300 dark:hover:bg-slate-900 dark:hover:text-primary-300 sm:hidden"
                aria-label="Open my donate page"
                title="Open my donate page"
              >
                <FiExternalLink className="h-4 w-4" />
              </a>
              <a
                href={donateUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden h-9 items-center justify-center gap-2 rounded-full border border-[#dbe3ec] px-3 text-xs font-bold text-[#475467] no-underline transition hover:border-[#168feb] hover:bg-[#eef7ff] hover:text-[#168feb] dark:border-slate-800 dark:text-slate-300 dark:hover:border-primary-500 dark:hover:bg-slate-900 dark:hover:text-primary-300 sm:inline-flex"
                aria-label="Open my donate page"
                title="Open my donate page"
              >
                <FiExternalLink className="h-4 w-4" />
                Donate
              </a>
            </>
          ) : null}

          {affiliateUrl ? (
            <>
              <a
                href={affiliateUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-9 w-9 items-center justify-center rounded-full text-[#475467] no-underline transition hover:bg-[#eef7ff] hover:text-[#168feb] dark:text-slate-300 dark:hover:bg-slate-900 dark:hover:text-primary-300 sm:hidden"
                aria-label="Open my affiliate link"
                title="Open my affiliate link"
              >
                <FiUserPlus className="h-4 w-4" />
              </a>
              <a
                href={affiliateUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden h-9 items-center justify-center gap-2 rounded-full border border-[#dbe3ec] px-3 text-xs font-bold text-[#475467] no-underline transition hover:border-[#168feb] hover:bg-[#eef7ff] hover:text-[#168feb] dark:border-slate-800 dark:text-slate-300 dark:hover:border-primary-500 dark:hover:bg-slate-900 dark:hover:text-primary-300 sm:inline-flex"
                aria-label="Open my affiliate link"
                title="Open my affiliate link"
              >
                <FiUserPlus className="h-4 w-4" />
                Affiliate
              </a>
            </>
          ) : null}

          <button
            type="button"
            className="relative flex h-9 w-9 items-center justify-center rounded-full text-[#475467] transition hover:bg-[#eef7ff] hover:text-[#168feb] dark:text-slate-300 dark:hover:bg-slate-900 dark:hover:text-primary-300"
            aria-label="Notifications"
          >
            <FiBell className="h-4 w-4" />
            <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-[#f43f5e]" />
          </button>

          <div className="flex min-w-0 items-center gap-2">
            <div className="hidden min-w-0 flex-col items-end sm:flex">
              <span className="max-w-[160px] truncate text-xs font-bold text-[#101828] dark:text-slate-100">
                {displayName || "Streamer"}
              </span>
              {username ? (
                <span className="max-w-[160px] truncate text-[11px] text-[#667085] dark:text-slate-400">
                  {user?.username ? `@${username}` : username}
                </span>
              ) : null}
            </div>
            <span className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-[#168feb] text-xs font-bold text-white">
              {avatarSrc ? (
                <img
                  src={avatarSrc}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                initials
              )}
            </span>
            <FiChevronDown className="h-4 w-4 text-[#98a2b3] dark:text-slate-500" />
          </div>
        </div>
      </nav>
    </header>
  );
}
